<script lang="ts">
  import { onMount } from "svelte";
  import type { SurfaceProject } from "./lib/types";
  import { snapshot, loadSnapshot } from "./lib/store";
  import { clearAuth, fetchServerBuild, hardReload, consumeHardReloadFlag } from "./lib/api";
  import { shortGenerated } from "./lib/format";
  import { reconcile as reconcilePinned } from "./lib/pinned";
  import Setup from "./views/Setup.svelte";
  import Today from "./views/Today.svelte";
  import Projects from "./views/Projects.svelte";
  import Areas from "./views/Areas.svelte";
  import Inbox from "./views/Inbox.svelte";
  import Search from "./views/Search.svelte";
  import ProjectDetail from "./views/ProjectDetail.svelte";
  import QuickAdd from "./views/QuickAdd.svelte";

  // Bump this when shipping material front-end changes. Worker exposes its
  // own SURFACE_BUILD; mismatch → drift banner with hard-reload button.
  const CLIENT_BUILD = "2026-05-07.1";

  type Tab = "today" | "projects" | "areas" | "inbox" | "search";
  let tab = $state<Tab>("today");
  let refreshing = $state(false);
  let quickAddOpen = $state(false);
  let detailProjectSlug = $state<string | null>(null);
  let serverBuild = $state<string | null>(null);
  let buildDriftDismissed = $state(false);
  let toast = $state<string | null>(null);
  let toastTimer: number | undefined;

  function showToast(msg: string, ms = 3500) {
    toast = msg;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (toast = null), ms);
  }

  onMount(async () => {
    const fromBuild = consumeHardReloadFlag();
    void loadSnapshot();
    serverBuild = await fetchServerBuild();
    if (fromBuild !== null) {
      const moved = fromBuild !== CLIENT_BUILD;
      showToast(
        moved
          ? `Updated · ${fromBuild} → ${CLIENT_BUILD}`
          : `Caches cleared · still on ${CLIENT_BUILD}`,
      );
    }
  });

  // Reconcile local pinned-today state every time a fresh snapshot arrives.
  $effect(() => {
    if ($snapshot.kind !== "ready") return;
    const pins = $snapshot.data.tasks
      .filter((t) => !!t.pinned_today)
      .map((t) => ({
        fingerprint: t.fingerprint,
        day: t.pinned_today as string,
        evening: !!t.pinned_evening,
      }));
    reconcilePinned(pins);
  });

  async function refresh() {
    if (refreshing) return;
    refreshing = true;
    await loadSnapshot({ fresh: true });
    serverBuild = await fetchServerBuild();
    refreshing = false;
    showToast("Refreshed", 1500);
  }

  function signOut() {
    if (!confirm("Clear stored token and base URL?")) return;
    clearAuth();
    window.location.reload();
  }

  function openProject(p: SurfaceProject) {
    detailProjectSlug = p.slug;
  }

  function closeProject() {
    detailProjectSlug = null;
  }

  // Only flag drift when the server is *newer* than this client. Server-older
  // means a frontend shipped without a worker bump — hard-reload wouldn't help
  // (we're already on the latest), so showing the banner is a false positive.
  // Build IDs are ISO-ish (`YYYY-MM-DD[.N]`); compare date then numeric suffix.
  function buildIsNewer(server: string, client: string): boolean {
    const [sd, ss = "0"] = server.split(".");
    const [cd, cs = "0"] = client.split(".");
    if (sd !== cd) return sd > cd;
    return Number(ss) > Number(cs);
  }
  const buildDrift = $derived(serverBuild !== null && buildIsNewer(serverBuild, CLIENT_BUILD));

  // Tab metadata — SF Symbol-style icons inlined as SVG paths.
  const TABS: { key: Tab; label: string; path: string; pathFill: string }[] = [
    {
      key: "today",
      label: "Today",
      path: "M12 2.5a1 1 0 0 1 1 1V5h2.5A2.5 2.5 0 0 1 18 7.5v9A2.5 2.5 0 0 1 15.5 19h-7A2.5 2.5 0 0 1 6 16.5v-9A2.5 2.5 0 0 1 8.5 5H11V3.5a1 1 0 0 1 1-1Zm3.5 4H8.5a1 1 0 0 0-1 1V9h9V7.5a1 1 0 0 0-1-1Zm1 4h-9v6.5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V10.5Z",
      pathFill: "M15.5 5H13V3.5a1 1 0 1 0-2 0V5H8.5A2.5 2.5 0 0 0 6 7.5v9A2.5 2.5 0 0 0 8.5 19h7a2.5 2.5 0 0 0 2.5-2.5v-9A2.5 2.5 0 0 0 15.5 5Zm.5 4.5h-9v-2A1 1 0 0 1 8.5 6.5h7a1 1 0 0 1 1 1v2Z",
    },
    {
      key: "projects",
      label: "Projects",
      path: "M4.5 6.5A2.5 2.5 0 0 1 7 4h3.2a2 2 0 0 1 1.4.6l1 1a1 1 0 0 0 .7.3H17a2.5 2.5 0 0 1 2.5 2.5v7.1A2.5 2.5 0 0 1 17 18H7a2.5 2.5 0 0 1-2.5-2.5v-9Zm2.5-1a1 1 0 0 0-1 1v9A1 1 0 0 0 7 16.5h10a1 1 0 0 0 1-1V8.4a1 1 0 0 0-1-1h-3.7a2.5 2.5 0 0 1-1.8-.7l-1-1a.5.5 0 0 0-.3-.2H7Z",
      pathFill: "M7 4h3.2a2 2 0 0 1 1.4.6l1 1a1 1 0 0 0 .7.3H17a2.5 2.5 0 0 1 2.5 2.5v7.1A2.5 2.5 0 0 1 17 18H7a2.5 2.5 0 0 1-2.5-2.5v-9A2.5 2.5 0 0 1 7 4Z",
    },
    {
      key: "areas",
      label: "Areas",
      path: "M5 6.5A1.5 1.5 0 0 1 6.5 5h3a1.5 1.5 0 0 1 1.5 1.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3Zm8 0A1.5 1.5 0 0 1 14.5 5h3A1.5 1.5 0 0 1 19 6.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 13 9.5v-3ZM5 14.5A1.5 1.5 0 0 1 6.5 13h3a1.5 1.5 0 0 1 1.5 1.5v3A1.5 1.5 0 0 1 9.5 19h-3A1.5 1.5 0 0 1 5 17.5v-3Zm8 0a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5v-3Z",
      pathFill: "M5 6.5A1.5 1.5 0 0 1 6.5 5h3a1.5 1.5 0 0 1 1.5 1.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3Zm8 0A1.5 1.5 0 0 1 14.5 5h3A1.5 1.5 0 0 1 19 6.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 13 9.5v-3ZM5 14.5A1.5 1.5 0 0 1 6.5 13h3a1.5 1.5 0 0 1 1.5 1.5v3A1.5 1.5 0 0 1 9.5 19h-3A1.5 1.5 0 0 1 5 17.5v-3Zm8 0a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5v-3Z",
    },
    {
      key: "inbox",
      label: "Inbox",
      path: "M5.5 5.5A2.5 2.5 0 0 1 8 3h8a2.5 2.5 0 0 1 2.5 2.5v6.6a2 2 0 0 1-.2.9l-2.4 4.7A2.5 2.5 0 0 1 13.6 19h-3.2a2.5 2.5 0 0 1-2.2-1.3l-2.4-4.7a2 2 0 0 1-.3-.9V5.5Zm2.5-1A1 1 0 0 0 7 5.5v6.6L9.4 17a1 1 0 0 0 .9.5h3.4a1 1 0 0 0 .9-.5L17 12.1V5.5a1 1 0 0 0-1-1H8Z",
      pathFill: "M8 3h8a2.5 2.5 0 0 1 2.5 2.5v6.6a2 2 0 0 1-.2.9l-2.4 4.7A2.5 2.5 0 0 1 13.6 19h-3.2a2.5 2.5 0 0 1-2.2-1.3l-2.4-4.7a2 2 0 0 1-.3-.9V5.5A2.5 2.5 0 0 1 8 3Z",
    },
    {
      key: "search",
      label: "Search",
      path: "M11 4a7 7 0 0 1 5.6 11.2l3.1 3.1a1 1 0 1 1-1.4 1.4l-3.1-3.1A7 7 0 1 1 11 4Zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z",
      pathFill: "M11 4a7 7 0 0 1 5.6 11.2l3.1 3.1a1 1 0 1 1-1.4 1.4l-3.1-3.1A7 7 0 1 1 11 4Z",
    },
  ];
</script>

{#if $snapshot.kind === "error" && $snapshot.message === "not_configured"}
  <Setup />
{:else if $snapshot.kind === "loading" || $snapshot.kind === "idle"}
  <div class="splash">
    <div class="loader">Loading…</div>
  </div>
{:else if $snapshot.kind === "error"}
  <div class="splash">
    <h2>Couldn't load.</h2>
    <p class="muted">{$snapshot.message}</p>
    <div class="splash-actions">
      <button class="ghost" onclick={refresh}>Retry</button>
      <button class="ghost danger" onclick={signOut}>Reset config</button>
    </div>
  </div>
{:else}
  {@const data = $snapshot.data}
  {@const detailProject = detailProjectSlug ? data.projects.find((p) => p.slug === detailProjectSlug) : null}
  <div class="app">
    <header class="navbar">
      <div class="navbar-inner">
        <span class="navbar-meta faint">snapshot · {shortGenerated(data.generated_at)}</span>
        <div class="navbar-actions">
          <button class="nav-btn" onclick={refresh} disabled={refreshing} aria-label="Refresh">
            {#if refreshing}
              <span class="spinner" aria-hidden="true"></span>
            {:else}
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path
                  d="M10 3.5a6.5 6.5 0 0 1 5.6 3.2l1-1A.75.75 0 0 1 18 6.4l-2 3.4a.75.75 0 0 1-.65.37H11.5a.75.75 0 0 1 0-1.5h2L13 8a5 5 0 0 0-7.6 1.7.75.75 0 0 1-1.36-.63A6.5 6.5 0 0 1 10 3.5Zm0 13a6.5 6.5 0 0 1-5.6-3.2l-1 1a.75.75 0 0 1-1.4-.7l2-3.4a.75.75 0 0 1 .65-.37H8.5a.75.75 0 0 1 0 1.5h-2L7 12a5 5 0 0 0 7.6-1.7.75.75 0 1 1 1.36.63A6.5 6.5 0 0 1 10 16.5Z"
                  fill="currentColor"
                />
              </svg>
            {/if}
          </button>
          <button class="nav-btn version" onclick={() => hardReload(CLIENT_BUILD)} title="Hard reload (clear caches)" aria-label="Build {CLIENT_BUILD}">
            v{CLIENT_BUILD}
          </button>
        </div>
      </div>
    </header>

    {#if buildDrift && !buildDriftDismissed}
      <div class="drift-banner">
        <div class="drift-text">
          <strong>New version available</strong>
          <span class="faint">app: {CLIENT_BUILD} · server: {serverBuild}</span>
        </div>
        <div class="drift-actions">
          <button class="primary-sm" onclick={() => hardReload(CLIENT_BUILD)}>Update</button>
          <button class="ghost-sm" onclick={() => (buildDriftDismissed = true)}>Later</button>
        </div>
      </div>
    {/if}

    <main class="main">
      {#if detailProject}
        <ProjectDetail {data} project={detailProject} onback={closeProject} />
      {:else if tab === "today"}
        <Today {data} />
      {:else if tab === "projects"}
        <Projects {data} onproject={openProject} />
      {:else if tab === "areas"}
        <Areas {data} onproject={openProject} />
      {:else if tab === "inbox"}
        <Inbox {data} />
      {:else if tab === "search"}
        <Search {data} onproject={openProject} />
      {/if}
    </main>

    {#if !detailProject}
      <button class="fab" aria-label="Add task" onclick={() => (quickAddOpen = true)}>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path d="M12 5.25a1 1 0 0 1 1 1V11h4.75a1 1 0 1 1 0 2H13v4.75a1 1 0 1 1-2 0V13H6.25a1 1 0 1 1 0-2H11V6.25a1 1 0 0 1 1-1Z" fill="currentColor"/>
        </svg>
      </button>
    {/if}

    {#if quickAddOpen}
      <QuickAdd {data} onclose={() => (quickAddOpen = false)} />
    {/if}

    {#if toast}
      <div class="toast" role="status">{toast}</div>
    {/if}

    {#if !detailProject}
      <nav class="tabbar" aria-label="Primary">
        {#each TABS as t (t.key)}
          {@const active = tab === t.key}
          <button
            type="button"
            class:active
            aria-current={active ? "page" : undefined}
            onclick={() => (tab = t.key)}
          >
            <svg class="tab-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path d={active ? t.pathFill : t.path} fill="currentColor" />
            </svg>
            <span class="tab-label">{t.label}</span>
          </button>
        {/each}
      </nav>
    {/if}
  </div>
{/if}

<style>
  .app { min-height: 100vh; display: flex; flex-direction: column; }
  .main { flex: 1; min-height: 0; }

  .splash {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 14px;
    padding: 24px;
    text-align: center;
  }
  .loader { color: var(--fg-dim); }
  .splash-actions { display: flex; gap: 12px; margin-top: 8px; }
  .ghost { background: var(--bg-elev); border: 1px solid var(--separator); padding: 11px 18px; border-radius: 10px; }
  .ghost.danger { color: var(--priority-high); }

  /* Navigation bar — translucent, blurred, iOS material */
  .navbar {
    position: sticky;
    top: 0;
    z-index: 30;
    background: var(--material-bar-bg);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    backdrop-filter: saturate(180%) blur(20px);
    border-bottom: 0.5px solid var(--separator);
    padding-top: var(--safe-top);
  }
  .navbar-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 14px 6px;
    height: 38px;
  }
  .navbar-meta {
    font-size: 12px;
    color: var(--fg-faint);
    letter-spacing: 0.01em;
  }
  .navbar-actions { display: flex; align-items: center; gap: 4px; }
  .nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--accent);
    font-size: 13px;
    height: 32px;
    min-width: 32px;
    padding: 0 8px;
    border-radius: 8px;
    transition: background 0.15s;
  }
  .nav-btn:active { background: var(--tap); }
  .nav-btn:disabled { opacity: 0.4; }
  .nav-btn.version { color: var(--fg-faint); font-size: 11px; font-variant-numeric: tabular-nums; }

  .spinner {
    width: 14px;
    height: 14px;
    border: 1.5px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .drift-banner {
    position: sticky;
    top: calc(var(--safe-top) + 38px);
    z-index: 25;
    background: rgba(28, 28, 30, 0.92);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-bottom: 0.5px solid var(--separator);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .drift-text { display: flex; flex-direction: column; font-size: 14px; gap: 2px; }
  .drift-text strong { font-weight: 600; }
  .drift-actions { display: flex; gap: 8px; flex-shrink: 0; }
  .primary-sm {
    background: var(--accent);
    color: var(--accent-fg);
    padding: 8px 16px;
    border-radius: 980px;
    font-weight: 600;
    font-size: 14px;
    min-height: 32px;
  }
  .primary-sm:active { background: var(--accent-pressed); }
  .ghost-sm {
    color: var(--accent);
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
  }

  /* Floating action button — refined shadow, larger tap target */
  .fab {
    position: fixed;
    right: 18px;
    bottom: calc(86px + var(--safe-bottom));
    width: 56px;
    height: 56px;
    border-radius: 28px;
    background: var(--accent);
    color: var(--accent-fg);
    box-shadow:
      0 4px 14px rgba(0, 0, 0, 0.40),
      0 1px 3px rgba(0, 0, 0, 0.30),
      inset 0 0 0 0.5px rgba(255, 255, 255, 0.10);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    transition: transform 0.12s ease, background 0.15s;
  }
  .fab:active {
    transform: scale(0.92);
    background: var(--accent-pressed);
  }

  /* Toast — pill, iOS-style, centered above tab bar */
  .toast {
    position: fixed;
    left: 50%;
    bottom: calc(96px + var(--safe-bottom));
    transform: translateX(-50%);
    background: rgba(44, 44, 46, 0.95);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    color: var(--fg);
    border: 0.5px solid var(--separator);
    padding: 10px 18px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
    z-index: 100;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
    animation: toastIn 0.20s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translate(-50%, 8px) scale(0.96); }
    to   { opacity: 1; transform: translate(-50%, 0)   scale(1); }
  }

  /* Tab bar — iOS pattern: translucent material, icon over label, hairline top */
  .tabbar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(50px + var(--safe-bottom));
    padding-bottom: var(--safe-bottom);
    background: var(--material-bar-bg);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    backdrop-filter: saturate(180%) blur(20px);
    border-top: 0.5px solid var(--separator);
    display: flex;
    z-index: 10;
  }
  .tabbar button {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: var(--fg-faint);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.01em;
    padding: 6px 0 4px;
    transition: color 0.15s;
  }
  .tab-icon {
    color: inherit;
    transition: transform 0.12s;
  }
  .tabbar button:active .tab-icon { transform: scale(0.92); }
  .tab-label { line-height: 1.1; }
  .tabbar button.active { color: var(--accent); }
</style>
